/*
 * SEO KEYWORD MAP — CONVERTER HORAS EM DIAS
 * URL: /converter-horas-em-dias
 * Primary: 72 horas são quantos dias (41K vol, KD 31)
 * Secondary: 48 horas são quantos dias (15K, KD 31)
 *            36 horas são quantos dias (4.4K, KD 23)
 * LSI: horas para dias, converter horas, quantas horas tem um dia,
 *       24 horas, conversão de tempo, dias para horas
 * Total addressable: ~60,400/mo
 * Page type: Time Conversion Tool + Guide
 */
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { formatNumber } from '../utils/timeCalculations';

const ConverterHorasDiasPage = () => {
  const [hours, setHours] = useState('');
  const [direction, setDirection] = useState('hoursToDays');

  const result = useMemo(() => {
    const val = parseFloat(hours) || 0;
    if (val <= 0) return null;
    if (direction === 'hoursToDays') {
      const totalDays = val / 24;
      // Work in whole minutes so a remainder never rounds up to "60min",
      // and drop zero parts so 72h reads "3 dias", not "3 dias e 0h".
      const totalMin = Math.round(val * 60);
      const fullDays = Math.floor(totalMin / 1440);
      const fullHours = Math.floor((totalMin % 1440) / 60);
      const remainMin = totalMin % 60;
      const timePart = fullHours > 0 || remainMin > 0
        ? `${fullHours}h${remainMin > 0 ? String(remainMin).padStart(2, '0') + 'min' : ''}`
        : '';
      const dayPart = fullDays > 0 ? `${fullDays} dia${fullDays > 1 ? 's' : ''}` : '';
      return {
        input: `${formatNumber(val, val % 1 ? 2 : 0)} horas`,
        days: formatNumber(totalDays, 4),
        fullDays,
        remainHours: fullHours,
        remainMin,
        formatted: [dayPart, timePart].filter(Boolean).join(' e ') || '0h'
      };
    } else {
      const totalHours = val * 24;
      return {
        input: `${val} dias`,
        totalHours: totalHours.toFixed(2),
        totalMinutes: (totalHours * 60).toFixed(0),
        formatted: `${formatNumber(totalHours)} horas (${formatNumber(totalHours * 60, 0)} minutos)`
      };
    }
  }, [hours, direction]);

  // Quick-reference conversions
  const commonConversions = [
    { hours: 12, days: '0,5 dia (meio dia)' },
    { hours: 24, days: '1 dia' },
    { hours: 36, days: '1 dia e 12 horas (1,5 dias)' },
    { hours: 48, days: '2 dias' },
    { hours: 72, days: '3 dias' },
    { hours: 96, days: '4 dias' },
    { hours: 120, days: '5 dias' },
    { hours: 168, days: '7 dias (1 semana)' },
    { hours: 240, days: '10 dias' },
    { hours: 360, days: '15 dias' },
    { hours: 720, days: '30 dias (1 mês)' },
  ];

  const faqs = [
    {
      question: '72 horas são quantos dias?',
      answer: '72 horas equivalem a exatamente 3 dias. Para chegar a esse resultado, basta dividir 72 por 24 (número de horas em um dia): 72 ÷ 24 = 3 dias completos.'
    },
    {
      question: '48 horas são quantos dias?',
      answer: '48 horas equivalem a exatamente 2 dias. O cálculo é simples: 48 ÷ 24 = 2 dias. Esse prazo é muito comum em prazos bancários, trabalhistas e de entrega.'
    },
    {
      question: '36 horas são quantos dias?',
      answer: '36 horas equivalem a 1 dia e 12 horas, ou 1,5 dias. O cálculo: 36 ÷ 24 = 1,5. Esse é o período de descanso da escala 12×36 (12 horas de trabalho seguidas de 36 horas de folga).'
    },
    {
      question: 'Como converter horas em dias?',
      answer: 'Para converter horas em dias, divida o número de horas por 24, pois cada dia tem 24 horas. Se o resultado for um número com decimais, a parte inteira são os dias completos e o restante pode ser convertido de volta para horas multiplicando por 24.'
    },
    {
      question: 'Quantas horas tem um dia?',
      answer: 'Um dia tem exatamente 24 horas, ou 1.440 minutos, ou 86.400 segundos. Esse é o padrão universal de medida de tempo baseado no ciclo completo de rotação da Terra.'
    },
    {
      question: '96 horas são quantos dias?',
      answer: '96 horas equivalem a 4 dias. O cálculo: 96 ÷ 24 = 4 dias completos. Esse prazo aparece com frequência em prazos de cancelamento e devolução de produtos.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Converter Horas em Dias",
    "url": "https://calculadoradehoras.net.br/converter-horas-em-dias",
    "description": "Ferramenta online gratuita para converter horas em dias e dias em horas. Descubra quantos dias são 72, 48, 36 horas e mais.",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "All",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "BRL" },
    "inLanguage": "pt-BR"
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>72 Horas São Quantos Dias? Conversor de Horas em Dias</title>
        <meta name="description" content="Descubra quantos dias são 72, 48, 36 horas e mais. Conversor online grátis de horas para dias com tabela de conversão e calculadora instantânea." />
        <link rel="canonical" href="https://calculadoradehoras.net.br/converter-horas-em-dias" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="72 Horas São Quantos Dias? Conversor de Horas em Dias" />
        <meta property="og:description" content="Converta horas em dias instantaneamente. Tabela completa + calculadora grátis." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/converter-horas-em-dias" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="72 Horas São Quantos Dias? Conversor de Horas em Dias" />
        <meta name="twitter:description" content="Converta horas em dias instantaneamente. Tabela completa + calculadora grátis." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={webAppSchema} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Converter Horas em Dias","item":"https://calculadoradehoras.net.br/converter-horas-em-dias"}
        ]
      }} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Converter Horas em Dias</span>
        </nav>

        {/* H1 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-dark-text mb-3">
            Converter Horas em Dias
          </h1>
          <p className="text-lg text-body-text max-w-2xl mx-auto">
            Descubra quantos dias equivalem a qualquer quantidade de horas. Tabela rápida, calculadora online e fórmula de conversão.
          </p>
          <span className="inline-block mt-2 text-sm text-body-text bg-light-bg px-3 py-1 rounded-full">Atualizado em 2026</span>
        </div>

        {/* Quick Answer Box — featured snippet bait */}
        <div className="bg-white border-l-4 border-blue-primary rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-heading font-bold text-blue-primary mb-2">72 Horas São Quantos Dias?</h2>
          <p className="text-3xl font-heading font-extrabold text-dark-text">3 dias</p>
          <p className="text-body-text mt-2">72 horas ÷ 24 horas por dia = <strong>3 dias</strong> exatos. Da mesma forma, 48 horas = 2 dias e 36 horas = 1 dia e 12 horas.</p>
        </div>

        {/* Calculator */}
        <div className="bg-white rounded-xl border-2 border-border-gray p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Calculadora: Horas para Dias</h2>

          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setDirection('hoursToDays')}
              className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-colors ${direction === 'hoursToDays' ? 'bg-blue-primary text-white' : 'bg-gray-100 text-body-text hover:bg-gray-200'}`}
            >
              Horas → Dias
            </button>
            <button
              onClick={() => setDirection('daysToHours')}
              className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-colors ${direction === 'daysToHours' ? 'bg-blue-primary text-white' : 'bg-gray-100 text-body-text hover:bg-gray-200'}`}
            >
              Dias → Horas
            </button>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-body-text mb-2">
              {direction === 'hoursToDays' ? 'Quantidade de Horas' : 'Quantidade de Dias'}
            </label>
            <input
              type="number"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder={direction === 'hoursToDays' ? 'Ex: 72' : 'Ex: 3'}
              min="0"
              step="0.5"
              className="w-full h-14 px-4 text-lg border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none transition-colors"
            />
          </div>

          {result && (
            <div className="mt-6 bg-light-bg border-l-4 border-success-green rounded-lg p-5">
              <p className="text-sm font-semibold text-body-text uppercase tracking-wide mb-1">Resultado</p>
              <p className="text-2xl font-heading font-bold text-dark-text">{result.formatted}</p>
              {direction === 'hoursToDays' && (
                <p className="text-sm text-body-text mt-2">{result.input} = {result.days} dias (decimal)</p>
              )}
            </div>
          )}
        </div>

        <AdSlot slotId="ad-slot-1" />

        {/* Conversion Table */}
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Tabela de Conversão: Horas para Dias</h2>
          <p className="text-body-text leading-relaxed mb-4">
            Consulte abaixo os valores mais pesquisados para converter horas em dias. A fórmula é sempre a mesma: divida o total de horas por 24.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-light-bg">
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Horas</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Equivalência em Dias</th>
                </tr>
              </thead>
              <tbody>
                {commonConversions.map((conv, i) => (
                  <tr key={i} className="hover:bg-light-bg transition-colors">
                    <td className="p-3 border-b border-border-gray font-bold text-dark-text">{`${conv.hours} horas`}</td>
                    <td className="p-3 border-b border-border-gray text-body-text">{conv.days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Content */}
        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Como Converter Horas em Dias</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A conversão de horas em dias é uma das operações de tempo mais simples. Como cada dia tem exatamente <strong>24 horas</strong>, basta dividir a quantidade de horas por 24 para obter o número de dias equivalentes.
            </p>
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Fórmula</h3>
              <p className="text-body-text text-lg"><strong>Dias = Horas ÷ 24</strong></p>
              <p className="text-body-text mt-2">Exemplo: 72 horas ÷ 24 = <strong>3 dias</strong></p>
            </div>
            <p className="text-body-text leading-relaxed mb-4">
              Quando o resultado não é um número inteiro, a parte decimal representa uma fração do dia. Para converter essa fração em horas, multiplique por 24. Por exemplo, 0,5 dias = 0,5 × 24 = 12 horas.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Exemplos Práticos de Conversão</h2>

            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">48 horas são quantos dias?</h3>
              <p className="text-body-text leading-relaxed">
                48 horas equivalem a <strong>2 dias</strong>. Esse é um dos prazos mais comuns no dia a dia: prazos bancários ("o boleto vence em 48 horas"), prazos de entrega e até notificações trabalhistas costumam usar esse período. O cálculo é direto: 48 ÷ 24 = 2.
              </p>
            </div>

            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">36 horas são quantos dias?</h3>
              <p className="text-body-text leading-relaxed">
                36 horas equivalem a <strong>1 dia e 12 horas</strong> (ou 1,5 dias). Esse período é relevante no contexto trabalhista: a escala 12×36, prevista no Art. 59-A da CLT, significa 12 horas de trabalho seguidas de 36 horas de descanso. Saber que 36 horas = 1 dia e meio ajuda o trabalhador a planejar seu descanso.
              </p>
            </div>

            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">72 horas são quantos dias?</h3>
              <p className="text-body-text leading-relaxed">
                72 horas equivalem a <strong>3 dias</strong> completos. Esse prazo aparece com frequência em políticas de cancelamento, prazos de entrega e orientações médicas. Sempre que você ler "72 horas", pode contar 3 dias a partir do momento de referência.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Quando Essa Conversão é Necessária?</h2>
            <p className="text-body-text leading-relaxed mb-4">
              Existem diversas situações práticas onde você precisa converter horas em dias ou entender quantos dias equivalem a um determinado número de horas. Veja as mais comuns.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border-2 border-border-gray rounded-lg p-5">
                <span className="text-2xl">⚖️</span>
                <h3 className="font-semibold text-dark-text mt-2 mb-1">Prazos Jurídicos</h3>
                <p className="text-sm text-body-text">Prazos processuais, notificações e direito do consumidor frequentemente usam horas em vez de dias.</p>
              </div>
              <div className="bg-white border-2 border-border-gray rounded-lg p-5">
                <span className="text-2xl">🏥</span>
                <h3 className="font-semibold text-dark-text mt-2 mb-1">Área Médica</h3>
                <p className="text-sm text-body-text">Resultados de exames, tempo de jejum e internações são frequentemente medidos em horas.</p>
              </div>
              <div className="bg-white border-2 border-border-gray rounded-lg p-5">
                <span className="text-2xl">📦</span>
                <h3 className="font-semibold text-dark-text mt-2 mb-1">Entregas e Logística</h3>
                <p className="text-sm text-body-text">Transportadoras informam prazos em horas (24h, 48h, 72h úteis) para entregas e fretes.</p>
              </div>
              <div className="bg-white border-2 border-border-gray rounded-lg p-5">
                <span className="text-2xl">👷</span>
                <h3 className="font-semibold text-dark-text mt-2 mb-1">Escalas de Trabalho</h3>
                <p className="text-sm text-body-text">Escalas 12×36 e turnos noturnos exigem converter horas de descanso em dias para planejamento.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Horas, Dias e a CLT</h2>
            <p className="text-body-text leading-relaxed mb-4">
              No contexto trabalhista brasileiro, a conversão entre horas e dias é fundamental para entender seus direitos. O Art. 58 da CLT estabelece que a jornada normal é de 8 horas diárias e 44 horas semanais.
            </p>
            <p className="text-body-text leading-relaxed mb-4">
              Cuidado com a diferença entre horas, dias corridos e dias úteis. Um prazo de 72 horas conta todas as horas seguidas, incluindo fins de semana. Já prazos em dias úteis pulam sábados, domingos e feriados: é assim que se contam os prazos processuais na Justiça do Trabalho (Art. 775 da CLT). Férias e aviso prévio, por outro lado, são contados em dias corridos.
            </p>
            <p className="text-body-text leading-relaxed">
              Para cálculos mais detalhados sobre jornada de trabalho, visite nossa <a href="/jornada-de-trabalho" className="text-blue-primary hover:underline">calculadora de jornada de trabalho</a> ou a <a href="/calculadora-de-horas-extras" className="text-blue-primary hover:underline">calculadora de horas extras</a>.
            </p>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        {/* FAQ */}
        <section className="my-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/converter-horas-em-dias" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default ConverterHorasDiasPage;
